import React, { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import './BiometricsPage.css';

const MIN_PHOTOS = 3;
const MAX_PHOTOS = 7;
const BIOMETRIC_MAX_DIMENSION = 2560;
const BIOMETRIC_JPEG_QUALITY = 0.92;
const FACE_DESCRIPTOR_VERSION = 'face-api-v1';

function loadFaceScript(source) {
  return new Promise((resolve, reject) => {
    const current = document.querySelector(`script[data-biometric-face-script="${source}"]`);
    if (current) {
      if (current.dataset.loaded === 'true') return resolve();
      current.addEventListener('load', resolve, { once: true });
      current.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = source;
    script.async = true;
    script.dataset.biometricFaceScript = source;
    script.addEventListener('load', () => { script.dataset.loaded = 'true'; resolve(); }, { once: true });
    script.addEventListener('error', () => reject(new Error('No se pudo cargar el motor facial')), { once: true });
    document.head.appendChild(script);
  });
}

function loadAssetImage(asset) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No se pudo leer una foto biométrica guardada'));
    image.src = assetUrl(asset);
  });
}

async function buildStoredFaceDescriptors(assets) {
  await Promise.all([
    loadFaceScript('/rrhh-assets/vendor/face-api/face-api.min.js'),
    loadFaceScript('/rrhh-assets/face-descriptor.js'),
  ]);
  if (typeof window.buildFaceDescriptorFromImage !== 'function') {
    throw new Error('No se pudo iniciar el generador facial');
  }
  return Promise.all(assets.map(async (asset) => ({
    descriptor: await window.buildFaceDescriptorFromImage(await loadAssetImage(asset)),
    descriptor_version: window.LANG_FACE_DESCRIPTOR_VERSION || FACE_DESCRIPTOR_VERSION,
    source_filename: assetFilename(asset),
  })));
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen capturada'));
    };
    image.src = url;
  });
}

async function prepareBiometricPhoto(file) {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('Seleccioná una imagen válida');
  }

  const image = await loadImage(file);
  const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = longestSide > BIOMETRIC_MAX_DIMENSION
    ? BIOMETRIC_MAX_DIMENSION / longestSide
    : 1;
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('No se pudo preparar la imagen');
  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error('No se pudo convertir la imagen a JPG'));
    }, 'image/jpeg', BIOMETRIC_JPEG_QUALITY);
  });
  const baseName = String(file.name || 'biometria').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

function requestErrorMessage(error, fallback) {
  if (error?.response?.status === 413) return 'La foto supera el tamaño permitido';
  return error?.response?.data?.error || fallback;
}

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function personName(person) {
  return [person.first_name, person.last_name].filter(Boolean).join(' ').trim()
    || person.nickname
    || person.email
    || 'Sin nombre';
}

function assetUrl(asset) {
  return typeof asset === 'string' ? asset : asset?.url;
}

function assetFilename(asset) {
  if (typeof asset === 'string') return asset.split('/').pop();
  return asset?.filename || String(asset?.url || '').split('/').pop();
}

function formatDate(value) {
  if (!value) return 'Sin registro';
  return new Intl.DateTimeFormat('es-UY', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function statusMeta(status) {
  if (status === 'linked') return { label: 'Vinculado', className: 'is-linked' };
  if (status === 'pending') return { label: 'En carga', className: 'is-pending' };
  return { label: 'No disponible', className: 'is-empty' };
}

function CaptureUploader({ assets, disabled, uploading, onUpload, onRemove }) {
  const [inputKey, setInputKey] = useState(0);

  const handleChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await onUpload(file);
    setInputKey((value) => value + 1);
  };

  return (
    <div className="biometrics-photo-picker">
      <label className={`biometrics-upload${disabled ? ' is-disabled' : ''}`}>
        <input
          key={inputKey}
          type="file"
          accept="image/*"
          capture="environment"
          disabled={disabled}
          onChange={handleChange}
        />
        <strong>{uploading ? 'Subiendo foto...' : 'Sacar foto'}</strong>
        <span>Cada foto queda guardada al subir</span>
      </label>
      {assets.length > 0 && (
        <div className="biometrics-photo-grid">
          {assets.map((asset, index) => (
            <figure key={`${assetUrl(asset)}-${index}`} className="biometrics-photo-preview">
              <img src={assetUrl(asset)} alt={`Foto biométrica ${index + 1}`} />
              <figcaption>{index + 1}</figcaption>
              <button
                className="biometrics-photo-remove"
                type="button"
                onClick={() => onRemove(asset)}
                disabled={disabled || uploading}
                aria-label={`Quitar foto ${index + 1}`}
              >
                ×
              </button>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}

function BiometricsPanel({ record, candidates, canEdit, onClose, onSaved }) {
  const isNew = !record?.biometric_id;
  const [personnelId, setPersonnelId] = useState(record?.personnel_id ? String(record.personnel_id) : '');
  const [draft, setDraft] = useState(record?.biometric_id ? record : null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const selectedPerson = isNew
    ? candidates.find((candidate) => String(candidate.personnel_id) === personnelId)
    : draft || record;
  const assets = Array.isArray(draft?.assets) ? draft.assets : [];
  const currentPersonnelId = draft?.personnel_id || record?.personnel_id || Number(personnelId);
  const currentStatus = statusMeta(draft?.status || record?.status);
  const canSave = canEdit
    && currentPersonnelId
    && assets.length >= MIN_PHOTOS
    && assets.length <= MAX_PHOTOS
    && !saving
    && !uploading
    && !generating;

  const startDraft = async (nextPersonnelId) => {
    const id = Number(nextPersonnelId || personnelId);
    if (!Number.isInteger(id) || id <= 0) return null;
    setSaving(true);
    setError('');
    try {
      const response = await api.post('/biometrics/draft', { personnel_id: id });
      setDraft(response.data);
      setPersonnelId(String(response.data.personnel_id));
      return response.data;
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo iniciar el alta biométrica');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!canSave) return;
    setGenerating(true);
    setError('');
    try {
      const descriptors = await buildStoredFaceDescriptors(assets);
      await api.post(`/biometrics/${currentPersonnelId}/finalize`, { descriptors });
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo generar la biometría');
    } finally {
      setGenerating(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!canEdit || uploading || assets.length >= MAX_PHOTOS) return;
    const target = draft || await startDraft(personnelId);
    if (!target?.personnel_id) return;
    setUploading(true);
    setError('');
    try {
      const photo = await prepareBiometricPhoto(file);
      const body = new FormData();
      body.append('photo', photo);
      const response = await api.post(`/biometrics/${target.personnel_id}/photos`, body);
      const nextAssets = Array.isArray(response.data?.assets) ? response.data.assets : [];
      try {
        const descriptors = await buildStoredFaceDescriptors(nextAssets);
        const descriptorResponse = await api.post(`/biometrics/${target.personnel_id}/descriptors`, { descriptors });
        setDraft(descriptorResponse.data);
      } catch (descriptorError) {
        const invalidAsset = nextAssets[nextAssets.length - 1];
        const invalidFilename = assetFilename(invalidAsset);
        if (invalidFilename) {
          const cleanupResponse = await api.delete(`/biometrics/${target.personnel_id}/photos/${encodeURIComponent(invalidFilename)}`);
          setDraft(cleanupResponse.data);
        }
        throw new Error('La foto no es utilizable para reconocimiento facial y fue descartada. Probá con otra, donde el rostro se vea de frente y con buena luz.');
      }
    } catch (requestError) {
      setError(requestErrorMessage(requestError, 'No se pudo preparar o subir la foto'));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (asset) => {
    if (!canEdit || uploading || !currentPersonnelId) return;
    const filename = assetFilename(asset);
    if (!filename) return;
    setUploading(true);
    setError('');
    try {
      const response = await api.delete(`/biometrics/${currentPersonnelId}/photos/${encodeURIComponent(filename)}`);
      setDraft(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo quitar la foto');
    } finally {
      setUploading(false);
    }
  };

  const unlink = async () => {
    if (!canEdit || saving || !currentPersonnelId) return;
    if (!window.confirm(`Se eliminará el alta biométrica de ${personName(draft || record || selectedPerson)}.`)) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(`/biometrics/${currentPersonnelId}`);
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo eliminar la biometría');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="biometrics-drawer-overlay" onClick={onClose}>
      <aside className="biometrics-drawer" onClick={(event) => event.stopPropagation()}>
        <div className="biometrics-drawer-head">
          <button className="btn btn-ghost biometrics-back" type="button" onClick={onClose}>Volver</button>
          <span className={`biometrics-status ${currentStatus.className}`}>{isNew && !draft ? 'Alta manual' : currentStatus.label}</span>
        </div>

        <form className="biometrics-form" onSubmit={save}>
          {isNew ? (
            <label>
              <span>Persona</span>
              <select
                className="form-input"
                value={personnelId}
                onChange={(event) => {
                  setPersonnelId(event.target.value);
                  setDraft(null);
                  if (event.target.value) startDraft(event.target.value);
                }}
                disabled={!canEdit || saving || uploading || Boolean(assets.length)}
              >
                <option value="">Elegir persona de Personal</option>
                {candidates.map((candidate) => (
                  <option key={candidate.personnel_id} value={candidate.personnel_id}>
                    {personName(candidate)}{candidate.nickname ? ` · ${candidate.nickname}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="biometrics-person-card">
              <div className="biometrics-avatar">{String((draft || record).nickname || (draft || record).first_name || '?').slice(0, 2).toUpperCase()}</div>
              <div>
                <h2>{personName(draft || record)}</h2>
                <p>{(draft || record).nickname || 'Sin alias'} · {(draft || record).operational_role || 'Sin rol operativo'}</p>
              </div>
            </div>
          )}

          {selectedPerson && isNew && (
            <div className="biometrics-person-card">
              <div className="biometrics-avatar">{String(selectedPerson.nickname || selectedPerson.first_name || '?').slice(0, 2).toUpperCase()}</div>
              <div>
                <h2>{personName(selectedPerson)}</h2>
                <p>{selectedPerson.nickname || 'Sin alias'} · {selectedPerson.operational_role || 'Sin rol operativo'}</p>
              </div>
            </div>
          )}

          <CaptureUploader
            assets={assets}
            disabled={!canEdit || !personnelId || saving || generating || assets.length >= MAX_PHOTOS}
            uploading={uploading}
            onUpload={uploadPhoto}
            onRemove={removePhoto}
          />
          <div className={`biometrics-photo-count${assets.length >= MIN_PHOTOS && assets.length <= MAX_PHOTOS ? ' is-ok' : ''}`}>
            {assets.length} de {MIN_PHOTOS}-{MAX_PHOTOS} fotos guardadas
          </div>
          {error && <div className="alert alert-error">{error}</div>}

          {draft?.created_at && (
            <div className="biometrics-meta">
              <span>Alta: {formatDate(draft.created_at)}</span>
              <span>Actualización: {formatDate(draft.updated_at)}</span>
            </div>
          )}

          {canEdit && (
            <div className="biometrics-actions">
              {currentPersonnelId && <button className="btn btn-ghost" type="button" onClick={unlink} disabled={saving || uploading || generating}>Eliminar alta</button>}
              <button className="btn btn-primary" type="submit" disabled={!canSave}>
                {generating ? 'Generando...' : 'Guardar y generar biometría'}
              </button>
            </div>
          )}
        </form>

        {generating && (
          <div className="biometrics-generating" role="status" aria-live="polite">
            <div className="spinner" />
            <strong>Generando datos biométricos</strong>
            <span>Estamos procesando las fotos guardadas.</span>
          </div>
        )}
      </aside>
    </div>
  );
}

export default function BiometricsPage() {
  const { user } = useAuth();
  const canEdit = canWrite(user, 'configuracion.biometria');
  const [records, setRecords] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [biometricsResponse, candidatesResponse] = await Promise.all([
        api.get('/biometrics'),
        api.get('/biometrics/candidates'),
      ]);
      setRecords(Array.isArray(biometricsResponse.data) ? biometricsResponse.data : []);
      setCandidates(Array.isArray(candidatesResponse.data) ? candidatesResponse.data : []);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo cargar biometría');
      setRecords([]);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const socket = io('/', { withCredentials: true });
    let timer = null;
    const reload = () => {
      clearTimeout(timer);
      timer = setTimeout(load, 350);
    };
    socket.on('personnel:updated', reload);
    socket.on('biometrics:updated', reload);
    return () => {
      clearTimeout(timer);
      socket.disconnect();
    };
  }, []);

  const filtered = records.filter((record) => {
    const q = normalize(search);
    if (!q) return true;
    return [
      personName(record),
      record.nickname,
      record.email,
      record.operational_role,
      record.private_code,
    ].some((value) => normalize(value).includes(q));
  });

  return (
    <div className="biometrics-page">
      <div className="page-header biometrics-header">
        <div>
          <h1 className="page-title">Biometría</h1>
          <p className="page-subtitle">Altas manuales de fotos vinculadas a Personal global.</p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" type="button" onClick={() => setCreating(true)} disabled={!candidates.length}>
            + Alta biométrica
          </button>
        )}
      </div>

      <section className="biometrics-toolbar">
        <input
          className="form-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar persona, alias o rol..."
        />
        <div className="biometrics-stat"><strong>{records.length}</strong><span>vinculadas</span></div>
        <div className="biometrics-stat"><strong>{candidates.length}</strong><span>pendientes</span></div>
      </section>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="biometrics-list" aria-label="Altas biométricas">
        {loading ? (
          <div className="biometrics-empty"><div className="spinner" /> Cargando biometría...</div>
        ) : filtered.length === 0 ? (
          <div className="biometrics-empty">
            {search ? 'Sin resultados.' : 'Todavía no hay altas biométricas.'}
          </div>
        ) : filtered.map((record) => (
          <button
            type="button"
            key={record.personnel_id}
            className="biometrics-row"
            onClick={() => setSelected(record)}
          >
            <span className="biometrics-row-main">
              <strong>{personName(record)}</strong>
              <small>{record.nickname || 'Sin alias'} · {record.operational_role || 'Sin rol operativo'}</small>
            </span>
            <span className="biometrics-row-extra">
              <span className={`biometrics-status ${statusMeta(record.status).className}`}>{statusMeta(record.status).label}</span>
              <small>{record.asset_count} foto{record.asset_count === 1 ? '' : 's'}</small>
            </span>
          </button>
        ))}
      </section>

      {(selected || creating) && (
        <BiometricsPanel
          record={selected || {}}
          candidates={candidates}
          canEdit={canEdit}
          onClose={() => { setSelected(null); setCreating(false); }}
          onSaved={load}
        />
      )}
    </div>
  );
}
